import { Switch, useTheme } from "@mui/material";

const TRACK_WIDTH = 44;
const TRACK_HEIGHT = 26;
const THUMB_SIZE = 20;
const THUMB_INSET = (TRACK_HEIGHT - THUMB_SIZE) / 2;

interface FlatSwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  /** Accessible name — a switch in a grid has no visible label of its own. */
  ariaLabel: string;
}

/**
 * The house on/off switch: 44×26, flat (no thumb shadow, no ripple), primary
 * track when on, a 2px focus ring for keyboard users, correct on both themes.
 *
 * The track is only 26px tall, so on touch surfaces give it a 44px-high
 * `<label>` to sit in (see NotificationPreferencesCard) rather than growing
 * the switch itself.
 */
const FlatSwitch = ({
  checked,
  onChange,
  disabled = false,
  ariaLabel,
}: FlatSwitchProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  // Light: a white thumb with no shadow needs a track dark enough to read against the card.
  const offTrack = isDark
    ? "rgba(255, 255, 255, 0.2)"
    : theme.palette.text.secondary;

  return (
    <Switch
      checked={checked}
      disabled={disabled}
      disableRipple
      onChange={(event) => onChange(event.target.checked)}
      slotProps={{ input: { "aria-label": ariaLabel, role: "switch" } }}
      sx={{
        width: TRACK_WIDTH,
        height: TRACK_HEIGHT,
        p: 0,
        flexShrink: 0,
        // The focus ring sits outside the track
        overflow: "visible",
        "& .MuiSwitch-switchBase": {
          p: 0,
          m: `${THUMB_INSET}px`,
          transitionDuration: "150ms",
          "&:hover": { backgroundColor: "transparent" },
          "&.Mui-checked": {
            transform: `translateX(${TRACK_WIDTH - TRACK_HEIGHT}px)`,
            color: "#ffffff",
            "&:hover": { backgroundColor: "transparent" },
            "& + .MuiSwitch-track": {
              backgroundColor: theme.palette.primary.main,
              opacity: 1,
            },
          },
          "&.Mui-focusVisible + .MuiSwitch-track": {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          },
          "&.Mui-disabled": { color: "#ffffff" },
          "&.Mui-disabled + .MuiSwitch-track": { opacity: 0.5 },
          "&.Mui-checked.Mui-disabled + .MuiSwitch-track": { opacity: 0.5 },
        },
        // MUI's hidden input spans three thumbs from one thumb to the left, so
        // it reaches 17px past the track and widens whatever holds the switch
        // (a bottom sheet scrolled sideways). Pin it to the track in either
        // position: the whole track still toggles, nothing overhangs.
        "& .MuiSwitch-input": {
          top: -THUMB_INSET,
          left: -THUMB_INSET,
          width: TRACK_WIDTH,
          height: TRACK_HEIGHT,
        },
        "& .Mui-checked .MuiSwitch-input": {
          left: -(THUMB_INSET + TRACK_WIDTH - TRACK_HEIGHT),
        },
        "& .MuiSwitch-thumb": {
          width: THUMB_SIZE,
          height: THUMB_SIZE,
          boxShadow: "none",
          backgroundColor: "#ffffff",
        },
        "& .MuiSwitch-track": {
          borderRadius: TRACK_HEIGHT / 2,
          backgroundColor: offTrack,
          opacity: 1,
          transition: "background-color 150ms ease",
        },
        "@media (prefers-reduced-motion: reduce)": {
          "& .MuiSwitch-switchBase, & .MuiSwitch-track": { transition: "none" },
        },
      }}
    />
  );
};

export default FlatSwitch;
